import React, {
  ComponentType,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Dropdown,
  Radio,
  Responsive
} from 'semantic-ui-react'
// Local
import { CONDITION } from '../../../constant'
import { isSmartDisplay } from '../../common/displayUtils'
import {
  encounterPractitionerTagsEnabled,
  encounterPractitionerTagOptions
} from '../../common/currentUserUtils'
import {
  conditionTagOptions
} from '../../common/practitionerTagUtils'
import {
  EncounterConditionDisplay,
  EncounterConditionPractitionerTagsRow,
  User
} from '../../authenticated/types'
import { RootState } from '../../../store'
import { ObjectValues } from '../../../types/types'

const SafeResponsive = Responsive as unknown as ComponentType<any>
const SafeDropdown = Dropdown as unknown as ComponentType<any>
const SafeRadio = Radio as unknown as ComponentType<any>

type DropdownOption = {
  key: string
  value: string
  text: string
}

type PhysicianConditionHccHeaderToolbarOwnProps = {
  mode?: string | null
  conditions: EncounterConditionDisplay[]
  onConditionAction: (
    action: ObjectValues<typeof CONDITION.ACTION>,
    conditions: EncounterConditionDisplay[]
  ) => void
}

type PhysicianConditionHccHeaderToolbarReduxProps = {
  currentUser: User
}

type PhysicianConditionHccHeaderToolbarProps =
  PhysicianConditionHccHeaderToolbarOwnProps &
  PhysicianConditionHccHeaderToolbarReduxProps

/**
 * Physician condition HCC header toolbar. Provides editors for encounter
 * condition practitioner tags intended for display in the an HCC condition
 * group header.
 *
 * The practitioner tag will apply to all of the conditions in the HCC group
 * and it is assumed that all of the conditions in the group have the same
 * practitioner tag
 *
 * @param mode View mode. Options: null or "smart". Default: null
 * @param conditions Array of conditions in the HCC group
 * @param onConditionAction Callback for update.
 *        Arguments: action, Array of conditions
 * @param mode View mode: smart or undefined. Default: undefined
 */
function PhysicianConditionHccHeaderToolbar(
  props: PhysicianConditionHccHeaderToolbarProps
): JSX.Element | null {
  const {
    mode: modeProp,
    conditions: conditionsProp,
    onConditionAction,
    currentUser
  } = props

  const [ conditions, setConditions ] = useState<EncounterConditionDisplay[]>(
    conditionsProp
  )

  const handleChangePractitionerTag = (
    event: React.SyntheticEvent,
    data: {
      value: string
    }
  ) => {
    event.stopPropagation()
    const action = CONDITION.ACTION.UPDATE_PRACTITIONER_TAGS
    const updatedConditions = conditions.map(c => {
      const updatedCondition: EncounterConditionDisplay = { ...c }
      if (updatedCondition.practitionerTags) {
        updatedCondition.practitionerTags = {
          ...updatedCondition.practitionerTags,
          practitionerResponseCode: data.value
        }
      } else {
        updatedCondition.practitionerTags = {
          id: c.id,
          tenantId: c.tenantId,
          encounterConditionId: c.id,
          practitionerResponseCode: data.value,
          creationDate: new Date(),
          modificationDate: new Date(),
          createdById: c.createdById,
          modifiedById: c.modifiedById
        } as EncounterConditionPractitionerTagsRow
      }
      return updatedCondition
    })
    setConditions(updatedConditions)
    onConditionAction(action, updatedConditions)
  }

  const renderRadio = (
    conditionsToRender: EncounterConditionDisplay[],
    tagOptions: DropdownOption[]
  ): JSX.Element => {
    const head = conditionsToRender[0]
    const currentTags = head.practitionerTags
    const value = currentTags && currentTags.practitionerResponseCode
      ? currentTags.practitionerResponseCode
      : ''
    const name = `${head.id}-practitionerResponseCode`

    return (
      <span className="physician-condition-toolbar-header">
        { tagOptions && tagOptions.map((option, i) => (
          <span key={ `${head.id}-${option.key}` }>
            <SafeResponsive
              as={ SafeRadio }
              className={ i > 0 ? 'm-12px-left' : '' }
              label={ option.text }
              name={ name }
              value={ option.value }
              checked={ option.value === value }
              onChange={ handleChangePractitionerTag }
            />
          </span>
        )) }
      </span>
    )
  }

  const renderDropdown = (
    conditionsToRender: EncounterConditionDisplay[],
    tagOptions: DropdownOption[]
  ): JSX.Element => {
    const head = conditionsToRender[0]
    const currentTags = head.practitionerTags
    const value = currentTags && currentTags.practitionerResponseCode
      ? currentTags.practitionerResponseCode
      : ''

    return (
      <span className="physician-condition-toolbar-header">
        <SafeResponsive
          as={ SafeDropdown }
          selection
          placeholder="Mark As"
          onChange={ handleChangePractitionerTag }
          options={ tagOptions }
          value={ value }
        />
      </span>
    )
  }

  const renderPractitionerTags = (
    conditionsToRender: EncounterConditionDisplay[]
  ): JSX.Element | null => {
    const tagsEnabled = encounterPractitionerTagsEnabled(currentUser)
    const tagOptions = encounterPractitionerTagOptions(currentUser)
    const buttonOptions = conditionTagOptions(conditionsToRender[0], tagOptions)
    if (tagsEnabled === true && buttonOptions && buttonOptions.length > 0) {
      const dropdownOptions: DropdownOption[] = buttonOptions.map(opt => ({
        key: opt.id,
        value: opt.value,
        text: opt.text
      }))
      return isSmartDisplay({ mode: modeProp })
        ? renderDropdown(
          conditionsToRender,
          dropdownOptions
        )
        : renderRadio(
          conditionsToRender,
          dropdownOptions
        )
    }
    return null
  }

  if (conditions && conditions.length > 0) {
    return renderPractitionerTags(conditions)
  }
  return null
}

const mapStateToProps = (state: RootState): PhysicianConditionHccHeaderToolbarReduxProps => ({
  currentUser: state.user
})

export default connect(
  mapStateToProps,
  {}
)(PhysicianConditionHccHeaderToolbar)
